import type { ResolvedRichTextValue } from "@/modules/kontent/types"
import type { ChangeEvent, FormEvent, FunctionComponent } from "react"

import { useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"

import AfterArrow from "@jobteaser/ui-kit/assets/icons/jds_afterArrow.svg"
import { ElementMessage } from "@jobteaser/ui-kit/components//ElementMessage/ElementMessage"
import { Button, BUTTON_TYPES, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"
import { InputField } from "@jobteaser/ui-kit/components/Input/InputField"

import { fetcher } from "@/modules/fetch/fetcher"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { RichText } from "@/modules/kontent/components/RichText"
import { useLocale } from "@/modules/locales/useLocale"

import { cleanLocalStorage, handleInputFieldChange, LANGUAGE_VALUES } from "./utils"

import styles from "./NewsletterContact.module.css"

type NewsletterContactProps = {
  actionUrl: string
  buttonTitle?: string
  successMessage: ResolvedRichTextValue
}

type NewsletterContactFormFieldId =
  // Visible fields
  | "email"
  // Hidden fields
  | "gclid"
  | "Language"
  | "Last_interaction_date"
  | "sfcid"
  | "utm_campaign"
  | "utm_content"
  | "utm_medium"
  | "utm_source"
  | "utm_term"

async function handleSubmit({
  event,
  actionUrl,
  formState,
}: {
  actionUrl: string
  event: FormEvent<HTMLFormElement>
  formState: Partial<Record<NewsletterContactFormFieldId, string>>
}): Promise<boolean> {
  event.preventDefault()

  const notFilledFields: NewsletterContactFormFieldId[] = ["email"].filter(
    field => !formState[field as NewsletterContactFormFieldId]
  ) as NewsletterContactFormFieldId[]

  if (notFilledFields.length) {
    return false
  }

  const data = {
    ...formState,
    actionUrl,
  }
  const body = JSON.stringify(data)
  const { status } = await fetcher(
    `/api/corporate/contact-form`,
    { body, method: "POST" },
    { serviceDomain: "INTERNAL_NEXT_API" }
  )
    .then(res => res.json())
    .catch(() => ({ status: false }))

  return status
}

export const NewsletterContact: FunctionComponent<NewsletterContactProps> = ({
  actionUrl,
  buttonTitle,
  successMessage,
}) => {
  const searchParams = useSearchParams()
  const locale = useLocale()
  const { t } = useTranslation(["corporate_form"])
  const [hasError, setHasError] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)
  const [formState, setFormState] = useState<Partial<Record<NewsletterContactFormFieldId, string>>>({
    Language: LANGUAGE_VALUES[locale],
    Last_interaction_date: "",
    gclid: `${searchParams.get("gclid") || ""}`,
    sfcid: `${searchParams.get("sfcid") || ""}`,
    utm_campaign: `${searchParams.get("utm_campaign") || ""}`,
    utm_content: `${searchParams.get("utm_content") || ""}`,
    utm_medium: `${searchParams.get("utm_medium") || ""}`,
    utm_source: `${searchParams.get("utm_source") || ""}`,
    utm_term: `${searchParams.get("utm_term") || ""}`,
  })

  useEffect(() => {
    cleanLocalStorage()
  }, [])

  useEffect(() => {
    setHasError(false)
  }, [formState])

  if (isSuccessful) {
    return (
      <RichText
        value={successMessage}
        wrapperProps={{
          tag: "div",
        }}
      />
    )
  }

  return (
    <form
      className={styles.form}
      method="POST"
      onSubmit={async event => {
        const status = await handleSubmit({ actionUrl, event, formState })

        if (!status) {
          setHasError(true)
        } else {
          setIsSuccessful(true)
        }
      }}
    >
      {hasError && (
        <ElementMessage.Error
          title={t("corporate_form.newsletter.errors.title")}
          withCloseButton
          onButtonClose={() => setHasError(false)}
        >
          {t("corporate_form.newsletter.errors.message")}
        </ElementMessage.Error>
      )}
      <div className={styles.fieldsWrapper}>
        <InputField
          commonProps={{
            hasError: hasError && !formState.email,
          }}
          containerProps={{
            className: styles.field,
          }}
          fieldProps={{
            className: styles.input,
            id: "email",
            onChange: (event: ChangeEvent<HTMLInputElement>) =>
              handleInputFieldChange({ event, formState, formUpdater: setFormState }),
            placeholder: t("corporate_form.newsletter.email.placeholder"),
            type: "email",
            value: formState.email,
          }}
          labelProps={{
            className: styles.label,
            label: t("corporate_form.newsletter.email.label"),
          }}
        />
        <Button
          className={styles.button}
          icon={AfterArrow}
          iconOnRight
          type={BUTTON_TYPES.SUBMIT}
          variant={BUTTON_VARIANT.NAKED}
        >
          {buttonTitle || t("corporate_form.buttons.submit")}
        </Button>
      </div>
    </form>
  )
}
